<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TasksAPI from 'kanban/api/tasks';
import {
  BILLING_FIELDS as F,
  parseBillingPlan,
  serializeBillingPlan,
} from '../billingFields';

const props = defineProps({
  taskId: { type: Number, default: null },
  customAttributes: { type: Object, default: () => ({}) },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['save', 'sync']);
const { t } = useI18n();

const recurrenceOptions = ['weekly', 'biweekly', 'monthly', 'annual'];
const reminderOptions = [-10, -5, -2, -1, 0, 1, 5, 10, 30];
const actionDelayOptions = [1, 5, 10, 30];
const charges = ref([]);
const editingId = ref(null);
const title = ref('');
const amount = ref('');
const paymentDate = ref('');
const recurrence = ref('monthly');
const unpaidResult = ref('');
const unpaidAction = ref('');
const reminderOffsets = ref([-5, -1, 0, 1]);
const actionDelayDays = ref(5);
const active = ref(false);
const error = ref('');
const sendingChargeId = ref(null);
const sendFeedback = ref(null);

watch(
  () => props.customAttributes?.[F.plan],
  value => {
    charges.value = parseBillingPlan(value).charges;
  },
  { immediate: true }
);

const resetForm = () => {
  editingId.value = null;
  title.value = '';
  amount.value = '';
  paymentDate.value = '';
  recurrence.value = 'monthly';
  unpaidResult.value = '';
  unpaidAction.value = '';
  reminderOffsets.value = [-5, -1, 0, 1];
  actionDelayDays.value = 5;
  active.value = false;
  error.value = '';
};

const emitPlan = nextCharges => {
  charges.value = nextCharges;
  emit('save', {
    ...props.customAttributes,
    [F.plan]: serializeBillingPlan(nextCharges),
  });
};

const toggleReminder = offset => {
  reminderOffsets.value = reminderOffsets.value.includes(offset)
    ? reminderOffsets.value.filter(item => item !== offset)
    : [...reminderOffsets.value, offset].sort((a, b) => a - b);
};

const newId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const saveCharge = () => {
  const parsedAmount = Number(String(amount.value).replace(',', '.'));
  if (
    !title.value.trim() ||
    !paymentDate.value ||
    !Number.isFinite(parsedAmount) ||
    parsedAmount <= 0 ||
    !recurrenceOptions.includes(recurrence.value) ||
    reminderOffsets.value.length === 0
  ) {
    error.value = t('KANBAN_BILLING.REQUIRED');
    return;
  }
  const existing = charges.value.find(charge => charge.id === editingId.value);
  const now = new Date().toISOString();
  const charge = {
    id: existing?.id || newId(),
    title: title.value.trim(),
    amount: parsedAmount,
    paymentDate: paymentDate.value,
    recurrence: recurrence.value,
    unpaidResult: unpaidResult.value.trim(),
    unpaidAction: unpaidAction.value.trim(),
    reminderOffsets: [...reminderOffsets.value],
    actionDelayDays: Number(actionDelayDays.value),
    active: active.value,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    cancelledAt: active.value ? null : existing?.cancelledAt || null,
    history: Array.isArray(existing?.history) ? existing.history : [],
  };
  emitPlan(
    existing
      ? charges.value.map(item => (item.id === existing.id ? charge : item))
      : [...charges.value, charge]
  );
  resetForm();
};

const editCharge = charge => {
  editingId.value = charge.id;
  title.value = charge.title || '';
  amount.value = String(charge.amount || '');
  paymentDate.value = charge.paymentDate || '';
  recurrence.value = recurrenceOptions.includes(charge.recurrence)
    ? charge.recurrence
    : 'monthly';
  unpaidResult.value = charge.unpaidResult || '';
  unpaidAction.value = charge.unpaidAction || '';
  reminderOffsets.value = Array.isArray(charge.reminderOffsets)
    ? [...charge.reminderOffsets]
    : [];
  actionDelayDays.value = actionDelayOptions.includes(charge.actionDelayDays)
    ? charge.actionDelayDays
    : 5;
  active.value = charge.active === true;
  error.value = '';
};

const cancelCharge = charge => {
  const now = new Date().toISOString();
  emitPlan(
    charges.value.map(item =>
      item.id === charge.id
        ? {
            ...item,
            active: false,
            cancelledAt: now,
            updatedAt: now,
            history: [
              ...(Array.isArray(item.history) ? item.history : []),
              {
                id: `${item.id}-cancelled-${Date.now()}`,
                type: 'cancelled',
                at: now,
              },
            ],
          }
        : item
    )
  );
  if (editingId.value === charge.id) resetForm();
};

const taskBillingAttributes = task => {
  const attributes = { ...(task?.custom_attributes || {}) };
  const livePlan = (task?.conversations || [])
    .map(conversation => conversation.recurring_billing)
    .find(value => value !== null && value !== undefined && value !== '');
  if (livePlan !== undefined) attributes[F.plan] = livePlan;
  return attributes;
};

const applyRemoteTask = task => {
  const attributes = taskBillingAttributes(task);
  const plan = parseBillingPlan(attributes[F.plan]);
  charges.value = plan.charges;
  emit('sync', attributes);
  return plan;
};

const wait = milliseconds =>
  new Promise(resolve => {
    window.setTimeout(resolve, milliseconds);
  });

const sendCharge = async charge => {
  if (!props.taskId || sendingChargeId.value) return;
  sendingChargeId.value = charge.id;
  sendFeedback.value = null;
  try {
    const requested = await TasksAPI.sendBilling(props.taskId, charge.id);
    const requestedPlan = applyRemoteTask(requested.data);
    const requestId = requestedPlan.charges.find(
      item => item.id === charge.id
    )?.manualSendRequestId;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response = await TasksAPI.show(props.taskId);
      const plan = applyRemoteTask(response.data);
      const current = plan.charges.find(item => item.id === charge.id);
      if (!current || current.manualSendRequestId !== requestId) {
        const event = [...(current?.history || [])]
          .reverse()
          .find(item => item.requestId === requestId);
        sendFeedback.value = {
          chargeId: charge.id,
          error: event?.type === 'error',
          message:
            event?.type === 'error'
              ? event.detail || t('KANBAN_BILLING.SEND_ERROR')
              : t('KANBAN_BILLING.SEND_SUCCESS'),
        };
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await wait(1000);
    }
    sendFeedback.value = {
      chargeId: charge.id,
      error: false,
      message: t('KANBAN_BILLING.SEND_PENDING'),
    };
  } catch (sendError) {
    sendFeedback.value = {
      chargeId: charge.id,
      error: true,
      message:
        sendError?.response?.data?.error || t('KANBAN_BILLING.SEND_ERROR'),
    };
  } finally {
    sendingChargeId.value = null;
  }
};

const formatDate = value => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const eventLabel = event => {
  const key = String(event?.type || 'scheduled');
  if (key === 'link_created') return t('KANBAN_BILLING.EVENTS.link_created');
  if (key === 'sent') return t('KANBAN_BILLING.EVENTS.sent');
  if (key === 'manual_sent') return t('KANBAN_BILLING.EVENTS.manual_sent');
  if (key === 'paid') return t('KANBAN_BILLING.EVENTS.paid');
  if (key === 'unpaid_action') return t('KANBAN_BILLING.EVENTS.unpaid_action');
  if (key === 'cancelled') return t('KANBAN_BILLING.EVENTS.cancelled');
  if (key === 'error') return t('KANBAN_BILLING.EVENTS.error');
  return t('KANBAN_BILLING.EVENTS.scheduled');
};

const recurrenceLabel = value => {
  if (value === 'weekly') return t('KANBAN_BILLING.RECURRENCES.weekly');
  if (value === 'biweekly') return t('KANBAN_BILLING.RECURRENCES.biweekly');
  if (value === 'annual') return t('KANBAN_BILLING.RECURRENCES.annual');
  return t('KANBAN_BILLING.RECURRENCES.monthly');
};

const reminderLabel = value => {
  if (value === -10) return t('KANBAN_BILLING.REMINDER_OPTIONS.-10');
  if (value === -5) return t('KANBAN_BILLING.REMINDER_OPTIONS.-5');
  if (value === -2) return t('KANBAN_BILLING.REMINDER_OPTIONS.-2');
  if (value === -1) return t('KANBAN_BILLING.REMINDER_OPTIONS.-1');
  if (value === 1) return t('KANBAN_BILLING.REMINDER_OPTIONS.1');
  if (value === 5) return t('KANBAN_BILLING.REMINDER_OPTIONS.5');
  if (value === 10) return t('KANBAN_BILLING.REMINDER_OPTIONS.10');
  if (value === 30) return t('KANBAN_BILLING.REMINDER_OPTIONS.30');
  return t('KANBAN_BILLING.REMINDER_OPTIONS.0');
};

const statusLabel = charge => {
  if (charge.active) return t('KANBAN_BILLING.STATUS.ACTIVE');
  if (charge.cancelledAt) return t('KANBAN_BILLING.STATUS.CANCELLED');
  return t('KANBAN_BILLING.STATUS.DRAFT');
};

const formTitle = computed(() =>
  editingId.value
    ? t('KANBAN_BILLING.EDIT_CHARGE')
    : t('KANBAN_BILLING.NEW_CHARGE')
);
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex items-center justify-between gap-3">
      <span class="font-medium text-n-slate-12">{{ formTitle }}</span>
      <Button
        v-if="editingId"
        variant="ghost"
        size="sm"
        :label="t('KANBAN_BILLING.NEW_CHARGE')"
        @click="resetForm"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        v-model="title"
        data-test="billing-title"
        :label="t('KANBAN_BILLING.TITLE')"
      />
      <Input
        v-model="amount"
        data-test="billing-amount"
        type="number"
        min="0.01"
        step="0.01"
        :label="t('KANBAN_BILLING.AMOUNT')"
      />
      <Input
        v-model="paymentDate"
        data-test="billing-payment-date"
        type="date"
        :label="t('KANBAN_BILLING.PAYMENT_DATE')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('KANBAN_BILLING.RECURRENCE') }}
        <select
          v-model="recurrence"
          data-test="billing-recurrence"
          class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
        >
          <option v-for="item in recurrenceOptions" :key="item" :value="item">
            {{ recurrenceLabel(item) }}
          </option>
        </select>
      </label>
      <Input
        v-model="unpaidResult"
        data-test="billing-unpaid-result"
        :label="t('KANBAN_BILLING.UNPAID_RESULT')"
      />
      <Input
        v-model="unpaidAction"
        data-test="billing-unpaid-action"
        :label="t('KANBAN_BILLING.UNPAID_ACTION')"
      />
    </div>

    <fieldset class="flex flex-col gap-2">
      <legend class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN_BILLING.REMINDERS') }}
      </legend>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <label
          v-for="offset in reminderOptions"
          :key="offset"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input
            :data-test="`billing-reminder-${offset}`"
            type="checkbox"
            :checked="reminderOffsets.includes(offset)"
            @change="toggleReminder(offset)"
          />
          {{ reminderLabel(offset) }}
        </label>
      </div>
    </fieldset>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('KANBAN_BILLING.ACTION_DELAY') }}
        <select
          v-model="actionDelayDays"
          data-test="billing-action-delay"
          class="h-10 rounded-lg bg-n-alpha-black2 px-3 outline outline-1 outline-n-weak"
        >
          <option v-for="days in actionDelayOptions" :key="days" :value="days">
            {{ t('KANBAN_BILLING.DAYS_AFTER', { days }) }}
          </option>
        </select>
      </label>
      <label
        class="flex items-center gap-2 self-end pb-2 text-sm text-n-slate-12"
      >
        <input v-model="active" data-test="billing-active" type="checkbox" />
        {{ t('KANBAN_BILLING.ACTIVE') }}
      </label>
    </div>

    <div class="flex items-center justify-end gap-3">
      <Button
        data-test="billing-save"
        :label="t('KANBAN_BILLING.APPLY')"
        :is-loading="saving"
        @click="saveCharge"
      />
    </div>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">{{ error }}</p>
    <p class="text-xs text-n-slate-11">
      {{ t('KANBAN_BILLING.DEFERRED_HINT') }}
    </p>

    <div class="border-t border-n-weak pt-4 flex flex-col gap-3">
      <span class="font-medium text-n-slate-12">{{
        t('KANBAN_BILLING.CHARGES')
      }}</span>
      <div
        v-for="charge in [...charges].reverse()"
        :key="charge.id"
        class="rounded-lg bg-n-alpha-black2 p-3 text-sm"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="flex flex-col">
            <span class="font-medium text-n-slate-12">{{ charge.title }}</span>
            <span class="text-xs text-n-slate-11">
              {{ formatMoney(charge.amount)
              }}{{ t('KANBAN_BILLING.SEPARATOR') }}
              {{ formatDate(charge.paymentDate)
              }}{{ t('KANBAN_BILLING.SEPARATOR') }}
              {{ recurrenceLabel(charge.recurrence) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">{{ statusLabel(charge) }}</span>
        </div>
        <div class="mt-3 flex flex-wrap gap-2">
          <Button
            data-test="billing-edit"
            variant="outline"
            size="sm"
            :label="t('KANBAN_BILLING.EDIT')"
            @click="editCharge(charge)"
          />
          <Button
            v-if="charge.active"
            data-test="billing-send-now"
            size="sm"
            :disabled="!taskId || saving"
            :is-loading="sendingChargeId === charge.id"
            :label="t('KANBAN_BILLING.SEND_NOW')"
            @click="sendCharge(charge)"
          />
          <Button
            v-if="charge.active"
            data-test="billing-cancel"
            variant="ghost"
            color="ruby"
            size="sm"
            :label="t('KANBAN_BILLING.CANCEL_CHARGE')"
            @click="cancelCharge(charge)"
          />
        </div>
        <p
          v-if="sendFeedback?.chargeId === charge.id"
          class="mt-2 text-xs"
          :class="sendFeedback.error ? 'text-n-ruby-11' : 'text-n-teal-11'"
        >
          {{ sendFeedback.message }}
        </p>
        <div class="mt-3 border-t border-n-weak pt-2">
          <span class="text-xs font-medium text-n-slate-12">{{
            t('KANBAN_BILLING.HISTORY')
          }}</span>
          <div v-if="charge.history?.length" class="mt-1 flex flex-col gap-1">
            <span
              v-for="event in [...charge.history].reverse()"
              :key="event.id"
              class="text-xs text-n-slate-11"
            >
              {{ eventLabel(event) }}{{ t('KANBAN_BILLING.SEPARATOR') }}
              {{ formatDate(event.dueDate || event.at) }}
              <template v-if="event.detail">
                {{ t('KANBAN_BILLING.SEPARATOR') }}{{ event.detail }}
              </template>
            </span>
          </div>
          <span v-else class="text-xs text-n-slate-11">{{
            t('KANBAN_BILLING.NO_HISTORY')
          }}</span>
        </div>
      </div>
      <span v-if="!charges.length" class="text-sm text-n-slate-11">{{
        t('KANBAN_BILLING.NO_CHARGES')
      }}</span>
    </div>
  </div>
</template>
